import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Ic from "./icons.jsx";
import { complaintsApi, type ComplaintAttachment } from "../api/complaints";
import { COMPLAINT_ALLOWED_TYPES, COMPLAINT_MAX_BYTES, COMPLAINT_MAX_FILES } from "../api/complaintUploadRules";
import { useToast } from "./Toast";

const ALLOWED = COMPLAINT_ALLOWED_TYPES;
const MAX_BYTES = COMPLAINT_MAX_BYTES;
const MAX_FILES = COMPLAINT_MAX_FILES;
const MAX_MB = Math.round(MAX_BYTES / (1024 * 1024));

type Props = {
  complaintId: string;
  attachments: ComplaintAttachment[];
  /** Hides the "Add file" control for callers without complaints:write/manage. */
  canUpload: boolean;
  onUploaded: () => Promise<void>;
};

/**
 * Post-create attachment manager for a complaint — pick file -> presign ->
 * PUT MinIO -> confirm. Bytes never touch the BE. Mirrors
 * `components/tm/TmAttachmentList.tsx`'s shape exactly, applied to
 * `complaintsApi`.
 */
export function ComplaintAttachmentList({ complaintId, attachments, canUpload, onUploaded }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  // Lazy-load thumbnail URLs for image attachments (same presigned-GET flow
  // as the download button, so MinIO permissions are enforced consistently).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      for (const a of attachments) {
        if (!a.contentType.startsWith("image/")) continue;
        if (thumbs[a.id]) continue;
        try {
          const { url } = await complaintsApi.attachmentDownloadUrl(complaintId, a.id);
          if (cancelled) return;
          setThumbs((prev) => ({ ...prev, [a.id]: url }));
        } catch {
          // skip — fallback icon will render
        }
      }
    }
    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, complaintId]);

  function fileIcon(type: string) {
    if (type === "application/pdf") return <Ic.pdf />;
    if (type.startsWith("image/")) return <Ic.image />;
    if (type === "application/zip") return <Ic.zip />;
    return <Ic.copy />;
  }

  async function handleFile(f: File) {
    if (!ALLOWED.includes(f.type)) {
      toast.error(t("complaints.attachments.errType", "File type not allowed"));
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(t("complaints.attachments.errTooLarge", { max: MAX_MB, defaultValue: `Max ${MAX_MB} MB` }));
      return;
    }
    if (attachments.length >= MAX_FILES) {
      toast.error(t("complaints.attachments.errMax", { max: MAX_FILES, defaultValue: `Max ${MAX_FILES} attachments` }));
      return;
    }
    setBusy(true);
    try {
      const presigned = await complaintsApi.presignAttachment(complaintId, {
        fileName: f.name, contentType: f.type, sizeBytes: f.size,
      });
      const res = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": f.type },
        body: f,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      await complaintsApi.confirmAttachment(complaintId, {
        objectKey: presigned.objectKey,
        fileName: f.name, contentType: f.type, sizeBytes: f.size,
      });
      toast.success(t("complaints.attachments.uploaded", "Attachment uploaded."));
      await onUploaded();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function download(a: ComplaintAttachment) {
    try {
      const { url } = await complaintsApi.attachmentDownloadUrl(complaintId, a.id);
      window.open(url, "_blank");
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  const atLimit = attachments.length >= MAX_FILES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {attachments.length === 0 && !canUpload ? (
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
          {t("complaints.attachments.empty", "No attachments.")}
        </div>
      ) : null}

      {attachments.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {attachments.map((a) => {
            const isImage = a.contentType.startsWith("image/");
            const thumbUrl = thumbs[a.id];
            return (
              <li key={a.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px",
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 13,
              }}>
                {isImage && thumbUrl ? (
                  <a
                    href={thumbUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ flex: "none", display: "block" }}
                    aria-label={a.fileName}
                  >
                    <img
                      src={thumbUrl}
                      alt=""
                      style={{
                        width: 44, height: 44, borderRadius: 6,
                        objectFit: "cover",
                        border: "1px solid var(--border)",
                        background: "var(--surface)",
                        display: "block",
                      }}
                    />
                  </a>
                ) : (
                  <span style={{
                    display: "grid", placeItems: "center",
                    width: 44, height: 44, borderRadius: 6,
                    background: "var(--surface)",
                    color: "var(--ink-3)", flex: "none",
                    border: "1px solid var(--border)",
                  }}>
                    {fileIcon(a.contentType)}
                  </span>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.fileName}
                  </div>
                  <div style={{ color: "var(--ink-4)", fontSize: 11 }}>{a.uploadedByName}</div>
                </div>
                <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  {(a.sizeBytes / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => download(a)}
                  className="btn sm"
                  style={{ padding: "4px 8px" }}
                >
                  {t("complaints.attachments.download", "Download")}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {canUpload && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED.join(",")}
            disabled={busy || atLimit}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
          <button
            type="button"
            className="btn"
            onClick={() => inputRef.current?.click()}
            disabled={busy || atLimit}
          >
            <Ic.upload />
            {atLimit
              ? t("complaints.attachments.atLimit", "Limit reached")
              : busy
                ? t("complaints.attachments.uploading", "Uploading…")
                : t("complaints.attachments.add", "Add file")}
          </button>
        </div>
      )}
    </div>
  );
}
